import { noopTelemetry } from '../chatTelemetry';
import { Metrics } from '../chatMetrics';
import { isArborHost } from '../isArborHost';
import { isTurnComplete, isAwaitingExternalTool, isAwaitingApproval } from '../utils/chatHelpers';
function shouldAttemptStreamRecovery(stopReason) {
  // Terminal reasons end the turn cleanly. EXTERNAL_TOOL / TOOLS_REQUIRE_APPROVAL
  // hand off to the client. Anything else at end-of-stream (TOOL_USE,
  // STRUCTURED_OUTPUT, STREAM_RETRY, ...) means the agent intended to keep
  // streaming — the connection ended prematurely, so resume from the last
  // seen message to recover any continuation persisted server-side.
  const reason = stopReason;
  return !isTurnComplete(reason) && !isAwaitingExternalTool(reason) && !isAwaitingApproval(reason);
}
export function createStreamingTransport(connection, logger, {
  retryDelay = 500,
  noStopReasonMaxAttempts = 8,
  toolUseMaxAttempts = 180,
  toolUseRetryDelay = 5000
} = {}, telemetry = noopTelemetry) {
  let lastSeenMessageId = null;
  let hasSeenAnyMessage = false;
  let currentConversationId = null;
  let currentTurnId = null;
  let expectingUserEcho = false;
  let reconnecting = false;
  let abortController = null;
  let postStreamQueue = [];
  let isProcessingStream = false;
  let pendingRetryTimer = null;
  let lastReconnectTerminal = false;
  function processStreamEvent(event, handler) {
    var _handler$onStreamEven;
    logger.transportStreamEvent(event);
    (_handler$onStreamEven = handler.onStreamEvent) === null || _handler$onStreamEven === void 0 || _handler$onStreamEven.call(handler, event);
    if (event.conversationMetadata) {
      currentConversationId = event.conversationMetadata.conversationId;
      currentTurnId = event.conversationMetadata.turnId;
      handler.onTurnStarted(event.conversationMetadata.conversationId, event.conversationMetadata.turnId, event.conversationMetadata.title);
    }
    if (event.message) {
      const msg = event.message;
      if (expectingUserEcho && msg.role === 'USER') {
        handler.onMessageUpdate({
          type: 'replace-optimistic',
          message: msg
        });
        expectingUserEcho = false;
      } else {
        handler.onMessageUpdate({
          type: 'append',
          message: msg
        });
      }
      hasSeenAnyMessage = true;
      const numericId = Number(msg.id);
      if (!isNaN(numericId)) {
        lastSeenMessageId = numericId;
      }
    }
  }
  async function processStream(stream, signal, handler) {
    let lastStopReason;
    isProcessingStream = true;
    try {
      for await (const event of stream) {
        var _event$message;
        processStreamEvent(event, handler);
        if ((_event$message = event.message) !== null && _event$message !== void 0 && _event$message.stopReason) {
          lastStopReason = event.message.stopReason;
        }
      }
    } catch (error) {
      const chatError = error;
      logger.transportError(chatError);
      telemetry.streamError(chatError);
      if (chatError.type === 'TRANSPORT_ERROR' && !reconnecting) {
        const reconnected = await tryReconnect(signal, handler);
        if (reconnected) return;
      }
      handler.onError(chatError);
      return;
    } finally {
      isProcessingStream = false;
    }
    if (signal.aborted) return;
    async function runRecoveryLoop(maxAttempts, stopReason, getDelay) {
      let resumed = false;
      for (let attempt = 0; attempt < maxAttempts; attempt++) {
        const delay = getDelay(attempt);
        if (delay > 0) {
          await new Promise(resolve => setTimeout(resolve, delay));
          if (signal.aborted) return false;
        }
        const result = tryReconnect(signal, handler);
        resumed = await result;
        if (resumed || lastReconnectTerminal) break;
      }
      logger.transportRecovery(resumed ? 'success' : 'failed', {
        stopReason,
        conversationId: currentConversationId,
        turnId: currentTurnId
      });
      if (!isArborHost()) {
        Metrics.counter(resumed ? 'stream-recovered' : 'stream-recovery-failed', {
          mode: 'streaming'
        }).increment();
      }
      return resumed;
    }
    if (lastStopReason && shouldAttemptStreamRecovery(lastStopReason) && !reconnecting) {
      if (await runRecoveryLoop(toolUseMaxAttempts, lastStopReason, attempt => attempt > 0 ? toolUseRetryDelay : 0)) {
        return;
      }
    }

    // Stream ended with no stop reason while in an active turn — the agent may
    // still be running (e.g., dispatched a background job that closed the
    // stream before producing a response). Attempt recovery before marking
    // the turn complete.
    if (!lastStopReason && currentConversationId !== null && currentTurnId !== null && hasSeenAnyMessage && !reconnecting) {
      if (await runRecoveryLoop(noStopReasonMaxAttempts, undefined, attempt => attempt > 0 ? retryDelay * 2 ** (attempt - 1) : 0)) {
        return;
      }
    }
    if (lastStopReason) {
      await drainPostStreamActions(handler);
      await handler.onStopReason(lastStopReason);
    } else {
      await drainPostStreamActions(handler);
      handler.onComplete();
    }
  }
  async function tryReconnect(_signal, handler) {
    var _handler$onReconnecti;
    if (currentConversationId === null || currentTurnId === null) {
      return false;
    }
    logger.transportReconnect(currentConversationId, currentTurnId);
    (_handler$onReconnecti = handler.onReconnecting) === null || _handler$onReconnecti === void 0 || _handler$onReconnecti.call(handler);
    // eslint-disable-next-line compat/compat
    abortController = new AbortController();
    reconnecting = true;
    let captured = null;
    let hadStopReason = false;
    lastReconnectTerminal = false;
    const captureHandler = Object.assign({}, handler, {
      onError(err) {
        captured = err;
        // INVALID_REQUEST or NOT_FOUND means the turn is no longer resumable
        // (already completed, cancelled, or not found). Stop retrying.
        if (err.type === 'INVALID_REQUEST' || err.type === 'NOT_FOUND') {
          lastReconnectTerminal = true;
        }
      },
      async onStopReason(stopReason) {
        // A recovery-eligible stop reason (e.g. TOOL_USE) at the end of a
        // resumed stream means the agent is still running — internal tool calls
        // were replayed from history or the backend dispatched more work. Don't
        // fire the real handler; return false so the outer loop reconnects again
        // to pick up the final ASSISTANT response.
        if (shouldAttemptStreamRecovery(stopReason)) {
          return;
        }
        hadStopReason = true;
        await handler.onStopReason(stopReason);
      },
      onComplete() {}
    });
    try {
      await processStream(connection.streamResume(Object.assign({
        conversationId: currentConversationId,
        turnId: currentTurnId
      }, lastSeenMessageId !== null ? {
        lastMessageId: lastSeenMessageId
      } : {}), abortController.signal), abortController.signal, captureHandler);
      return captured === null && hadStopReason;
    } catch (_unused) {
      return false;
    } finally {
      reconnecting = false;
    }
  }
  async function drainPostStreamActions(handler) {
    while (postStreamQueue.length > 0) {
      const action = postStreamQueue.shift();
      try {
        await action();
      } catch (error) {
        handler.onError(error);
        postStreamQueue = [];
        return;
      }
    }
  }
  const transport = {
    activeStatus: 'STREAMING',
    async send(request, handler) {
      var _request$externalSkil;
      expectingUserEcho = !request.skipUserEcho;
      // eslint-disable-next-line compat/compat
      abortController = new AbortController();
      const controller = abortController;
      const chirpRequest = Object.assign({
        agentId: request.agentId
      }, request.conversationId ? {
        conversationId: request.conversationId
      } : {}, request.collectionId ? {
        collectionId: request.collectionId
      } : {}, {
        message: request.message
      }, request.turnMetadata ? {
        turnMetadata: request.turnMetadata
      } : {}, request.conversationMetadata ? {
        conversationMetadata: request.conversationMetadata
      } : {}, request.messageContexts ? {
        messageContexts: request.messageContexts
      } : {}, request.fileReferences ? {
        fileReferences: request.fileReferences
      } : {}, {
        streaming: true
      }, request.configVersion != null ? {
        configVersion: request.configVersion
      } : {}, {
        externalToolSpecs: request.externalToolSpecs,
        externalSkillSpecs: (_request$externalSkil = request.externalSkillSpecs) !== null && _request$externalSkil !== void 0 ? _request$externalSkil : []
      }, request.externalSubagentSpecs ? {
        externalSubagentSpecs: request.externalSubagentSpecs
      } : {});
      await processStream(connection.streamSendMessage(chirpRequest, controller.signal), controller.signal, handler);
    },
    async sendToolResponse(request, handler) {
      logger.transportToolResponse(request);
      if (isProcessingStream) {
        postStreamQueue.push(() => doSendToolResponse(request, handler));
        return;
      }
      await doSendToolResponse(request, handler);
    },
    async sendToolApproval(request, handler) {
      if (isProcessingStream) {
        postStreamQueue.push(() => doSendToolApproval(request, handler));
        return;
      }
      await doSendToolApproval(request, handler);
    },
    async resume(request, handler) {
      logger.transportResume(request);
      // eslint-disable-next-line compat/compat
      abortController = new AbortController();
      const controller = abortController;
      currentConversationId = request.conversationId;
      currentTurnId = request.turnId;
      if (request.lastMessageId != null) {
        lastSeenMessageId = request.lastMessageId;
      }
      await processStream(connection.streamResume(Object.assign({
        conversationId: request.conversationId,
        turnId: request.turnId
      }, request.lastMessageId != null ? {
        lastMessageId: request.lastMessageId
      } : {}), controller.signal), controller.signal, handler);
    },
    async cancelTurn(request) {
      logger.transportCancel(request);
      try {
        await connection.cancelTurn({
          conversationId: request.conversationId,
          turnId: request.turnId,
          reason: request.reason
        });
      } catch (_unused2) {
        // best-effort
      }
    },
    stop() {
      var _abortController;
      if (pendingRetryTimer !== null) {
        clearTimeout(pendingRetryTimer);
        pendingRetryTimer = null;
      }
      (_abortController = abortController) === null || _abortController === void 0 || _abortController.abort();
      abortController = null;
      postStreamQueue = [];
    },
    clear() {
      var _abortController2;
      if (pendingRetryTimer !== null) {
        clearTimeout(pendingRetryTimer);
        pendingRetryTimer = null;
      }
      (_abortController2 = abortController) === null || _abortController2 === void 0 || _abortController2.abort();
      abortController = null;
      lastSeenMessageId = null;
      hasSeenAnyMessage = false;
      currentConversationId = null;
      currentTurnId = null;
      expectingUserEcho = false;
      reconnecting = false;
      postStreamQueue = [];
    },
    async submitFeedback(request) {
      try {
        await connection.submitFeedback(request);
      } catch (_unused3) {
        // fire-and-forget
      }
    }
  };
  async function doSendToolResponse(request, handler, retryCount = 0) {
    var _request$externalSkil2;
    // eslint-disable-next-line compat/compat
    abortController = new AbortController();
    const controller = abortController;

    // The backend may reject tool responses with INVALID_REQUEST when the
    // turn status has not yet been persisted to AWAITING_TOOL_RESULT. This
    // is a race between the pull worker DB write and the client sending the
    // tool response after receiving the EXTERNAL_TOOL stream event. Wrap the
    // handler to intercept this specific error and retry after a short delay.
    const retryableHandler = retryCount < 8 ? Object.assign({}, handler, {
      onError(error) {
        if (error.type === 'INVALID_REQUEST') {
          pendingRetryTimer = setTimeout(() => {
            pendingRetryTimer = null;
            if (abortController === null) return;
            void doSendToolResponse(request, handler, retryCount + 1);
          }, retryDelay);
          return;
        }
        if (error.type === 'TRANSPORT_ERROR') {
          pendingRetryTimer = setTimeout(() => {
            pendingRetryTimer = null;
            if (abortController === null) return;
            void doSendToolResponse(request, handler, retryCount + 1);
          }, toolUseRetryDelay);
          return;
        }
        handler.onError(error);
      }
    }) : handler;
    await processStream(connection.streamSendToolResponse(Object.assign({
      conversationId: request.conversationId,
      turnId: request.turnId,
      toolResults: request.toolResults,
      externalToolSpecs: request.externalToolSpecs,
      externalSkillSpecs: (_request$externalSkil2 = request.externalSkillSpecs) !== null && _request$externalSkil2 !== void 0 ? _request$externalSkil2 : []
    }, request.externalSubagentSpecs ? {
      externalSubagentSpecs: request.externalSubagentSpecs
    } : {}, request.turnMetadata ? {
      turnMetadata: request.turnMetadata
    } : {}, request.messageContexts ? {
      messageContexts: request.messageContexts
    } : {}), controller.signal), controller.signal, retryableHandler);
  }
  async function doSendToolApproval(request, handler) {
    // eslint-disable-next-line compat/compat
    abortController = new AbortController();
    const controller = abortController;
    await processStream(connection.streamSendToolApproval(Object.assign({
      conversationId: request.conversationId,
      turnId: request.turnId,
      approvals: request.approvals
    }, request.turnMetadata ? {
      turnMetadata: request.turnMetadata
    } : {}, request.messageContexts ? {
      messageContexts: request.messageContexts
    } : {}), controller.signal), controller.signal, handler);
  }
  return transport;
}